import React, { useContext, useMemo } from "react"
import { useController } from "react-hook-form"
import { DimensionsContext } from "../../utils/mobileUtils"
import { DatePickerModal } from "react-native-paper-dates"
import moment from "moment"

// Lu par moment et non par `new Date` : `new Date("2026-09-22")` est minuit UTC,
// donc la veille au soir aux Antilles, et le calendrier s'ouvrait sur le mauvais
// jour. moment lit une date seule comme minuit local.
export const toPickerDate = (value) => (value ? moment(value).toDate() : null)

const DatePickerModalM = React.forwardRef((props, ref) => {
    const {
        label,
        control,
        name,
        cancelLabel = "Annuler",
        style,
        saveLabel = "Confirmer",
        closeIcon,
        editIcon,
        onChangeAdditional = () => {},
        contentContainerStyle,
        allowEditing = false,
        calendarIcon,
        locale = "fr",
        mode = "single",
        visible,
        onDismiss = () => {},
        // Semaine commençant le LUNDI. C'est la convention française, et le
        // défaut de la librairie est le dimanche : sans ça, le parent lit le
        // calendrier décalé d'un jour.
        startWeekOnMonday = true,
        // Jours non sélectionnables, en jours RÉELS (0 = dimanche … 6 = samedi),
        // comme `Date.getDay()`. Interdire à la source vaut mieux que corriger
        // après coup : une date refusée puis déplacée oblige à expliquer un
        // geste que le parent n'a pas demandé.
        //
        // ⚠️ La librairie, elle, indexe la POSITION dans la semaine affichée :
        // avec la semaine au lundi, son index 0 est lundi, pas dimanche. Passer
        // 0 en croyant viser le dimanche fait disparaître le lundi — vécu. On
        // convertit donc ici, une fois, pour que l'appelant parle en jours.
        disableWeekDays,
        // Uncontrolled mode props (used when control/name are not provided)
        value,
        onConfirm,
        validRange,
        // Opt-in: apply the date only when the tutor presses `saveLabel`.
        // `onChange` fires on every day tap, so by default the picker commits (and
        // its caller usually closes) before the save button is ever reached —
        // which makes `saveLabel` decorative. Left as the default so the existing
        // callers keep the behaviour they were written against.
        applyOnSaveOnly = false,
    } = props

    const isControlled = !!control && !!name
    const field = isControlled ? useController({ control, name }).field : null

    // La date brute, dans les deux modes — le memo ci-dessous s'indexe sur CETTE
    // chaîne, pas sur l'objet Date qu'il en construit.
    const dateString = isControlled ? field?.value : value

    // Mémoïsé : `toPickerDate` rend un objet NEUF à chaque appel, et le contenu
    // de la librairie re-sème sa propre sélection depuis `date` dès que
    // l'IDENTITÉ de cette prop change (`DatePickerModalContent`, effet sur
    // `[date, startDate, endDate, dates]`). Reconstruit à chaque render, il
    // ramenait le jour qu'on venait de toucher sur la date semée, sans rien dire
    // — il suffisait d'un re-render du parent pendant que le calendrier est
    // ouvert (une réponse d'API qui atterrit, un redimensionnement de fenêtre sur
    // le web, ce composant lisant lui-même DimensionsContext), et « Confirmer »
    // enregistrait alors la date semée à la place du choix de l'utilisateur.
    const dateValue = useMemo(() => toPickerDate(dateString), [dateString])

    const handleConfirm = isControlled
        ? (e) => {
              const dd = new moment(e.date).format("YYYY-MM-DD")
              field.onChange(dd)
              onChangeAdditional(dd)
          }
        : ({ date }) => {
              if (!date) return
              const dd = moment(date).format("YYYY-MM-DD")
              onConfirm?.(dd)
              onChangeAdditional(dd)
          }

    // Jour réel → position dans la semaine affichée.
    const disabledColumns = disableWeekDays?.map((day) =>
        startWeekOnMonday ? (day + 6) % 7 : day
    )

    const dimensions = useContext(DimensionsContext)
    return (
        <DatePickerModal
            label={label}
            cancelLabel={cancelLabel}
            saveLabel={saveLabel}
            closeIcon={closeIcon}
            editIcon={editIcon}
            allowEditing={allowEditing}
            calendarIcon={calendarIcon}
            locale={locale}
            mode={mode}
            visible={visible}
            onDismiss={onDismiss}
            date={dateValue}
            startWeekOnMonday={startWeekOnMonday}
            disableWeekDays={disabledColumns}
            validRange={validRange}
            onConfirm={handleConfirm}
            onChange={applyOnSaveOnly ? undefined : handleConfirm}
        />
    )
})

export default DatePickerModalM
